import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AdminService } from 'src/app/services';

interface WrappedSnapshot {
  merchantName: string;
  merchantLogo?: string;
  periodLabel: string;
  stats: {
    stamps: number;
    points: number;
    customers: number;
    newCustomers: number;
    rewards: number;
    unredeemedRewards?: number;
    redemptions: number;
    vouchersSold: number;
  };
  capabilities?: {
    stamps?: boolean;
    points?: boolean;
  };
  highlights: {
    busiestDay: string;
    busiestDayMetrics?: {
      stamps: number;
      points: number;
      rewards: number;
      redemptions: number;
      customers: number;
    };
    favouriteReward: string;
    promotions?: {
      count: number;
    };
    birthdayClub?: {
      enabled: boolean;
      rewards: number;
      messages?: number;
    };
    campaignMessages?: {
      count: number;
      recipients: number;
      delivered: number;
    };
    giftVouchers?: {
      count: number;
      totalSold: number;
    };
    giftedRewards?: {
      count: number;
      gifters: number;
      recipients: number;
      topReward?: string;
    };
    topInterests?: string[];
    automations?: {
      count: number;
      recipients: number;
    };
  };
}

interface WrappedSlide {
  eyebrow: string;
  title: string;
  metric: string;
  detail: string;
  textMetric?: boolean;
}

@Component({
  selector: 'wrapped-public',
  templateUrl: './wrapped-public.component.html',
  styleUrls: ['./wrapped-public.component.css'],
})
export class WrappedPublicComponent implements OnInit, OnDestroy {
  loading = true;
  error = '';
  snapshot?: WrappedSnapshot;
  currentSlide = 0;
  introPlaying = false;
  cardFlightCycle = 0;
  transitionDirection: 'forward' | 'back' = 'forward';
  private introTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private route: ActivatedRoute,
    private adminService: AdminService,
  ) {}

  ngOnInit(): void {
    const token = this.route.snapshot.paramMap.get('token');
    if (!token) {
      this.error = 'Wrapped link not found';
      this.loading = false;
      return;
    }

    this.adminService
      .getWrappedDocument(token)
      .subscribe({
        next: (res: any) => {
          this.snapshot = res.snapshot;
          this.playIntro();
        },
        error: (res: HttpErrorResponse) => {
          this.error = res.error?.message || 'Wrapped link not found';
        },
      })
      .add(() => (this.loading = false));
  }

  ngOnDestroy(): void {
    if (this.introTimer) {
      clearTimeout(this.introTimer);
    }
  }

  playIntro(): void {
    if (this.introTimer) {
      clearTimeout(this.introTimer);
    }

    this.introPlaying = true;
    this.introTimer = setTimeout(() => {
      this.introPlaying = false;
    }, 5000);
  }

  replay(): void {
    this.transitionDirection = 'back';
    this.currentSlide = 0;
    this.playIntro();
  }

  next(): void {
    const nextSlide = Math.min(this.currentSlide + 1, this.slides.length - 1);
    if (nextSlide === this.currentSlide) {
      return;
    }

    this.transitionDirection = 'forward';
    this.currentSlide = nextSlide;
    this.playCardFlight();
  }

  previous(): void {
    const previousSlide = Math.max(this.currentSlide - 1, 0);
    if (previousSlide === this.currentSlide) {
      return;
    }

    this.transitionDirection = 'back';
    this.currentSlide = previousSlide;
    this.playCardFlight();
  }

  playCardFlight(): void {
    this.cardFlightCycle += 1;
  }

  get cardFlightClass(): string {
    if (this.cardFlightCycle === 0) {
      return '';
    }

    const direction = this.transitionDirection === 'back' ? 'back' : 'forward';
    const parity = this.cardFlightCycle % 2 === 0 ? 'even' : 'odd';
    return `card-flight card-flight-${direction}-${parity}`;
  }

  async share(): Promise<void> {
    const title = `${this.snapshot?.merchantName} Wrapped`;
    const text = `${this.snapshot?.merchantName}'s Remy Wrapped is here: a look back at the loyalty moments, rewards, customers, and comebacks from the year.`;
    const url = window.location.href;
    const shareMessage = `${text}\n\n${url}`;

    if (navigator.share) {
      await navigator.share({ title, text, url });
      return;
    }

    await navigator.clipboard.writeText(shareMessage);
  }

  get envelopeOpen(): boolean {
    return this.introPlaying || this.currentSlide > 0;
  }

  get slides(): WrappedSlide[] {
    if (!this.snapshot) {
      return [];
    }

    const stats = this.snapshot.stats;
    const highlights = this.snapshot.highlights;
    const loyaltySlide = this.loyaltySlide;

    return [
      {
        eyebrow: 'Your year with Remy wrapped',
        title: `${this.snapshot.merchantName}, your Wrapped has entered the chat`,
        metric: '',
        detail:
          'A quick tour of the loyalty moments, customer comebacks, and tiny dopamine hits your team created.',
      },
      {
        eyebrow: 'Return-visit behaviour',
        title: loyaltySlide.title,
        metric: loyaltySlide.metric,
        detail: loyaltySlide.detail,
      },
      {
        eyebrow: 'The fan club',
        title: 'Your customer circle got more popular',
        metric: stats.customers.toLocaleString(),
        detail: this.customerDetail,
      },
      ...(this.customerInterestsSlide ? [this.customerInterestsSlide] : []),
      {
        eyebrow: 'Main character day',
        title: highlights.busiestDay,
        metric: this.busiestDayMetric,
        detail: this.busiestDayDetail,
      },
      {
        eyebrow: 'Reward economy',
        title: 'You handed out reasons to smile',
        metric: stats.rewards.toLocaleString(),
        detail: `${this.countText(stats.redemptions, 'reward was', 'rewards were')} redeemed. The crowd favourite was ${highlights.favouriteReward}. A tasteful choice.`,
      },
      ...(this.giftedRewardSlide ? [this.giftedRewardSlide] : []),
      this.voucherOrGrowthSlide,
      ...(this.promotionSlide ? [this.promotionSlide] : []),
      this.birthdayOrAutomationSlide,
      ...(this.campaignSlide ? [this.campaignSlide] : []),
      this.finalSlide,
    ];
  }

  get busiestDayMetric(): string {
    const metrics = this.snapshot?.highlights.busiestDayMetrics;
    if (!metrics) {
      return '';
    }

    return Math.max(
      metrics.customers || 0,
      metrics.stamps || 0,
      metrics.points || 0,
      metrics.rewards || 0,
      metrics.redemptions || 0,
    ).toLocaleString();
  }

  get customerDetail(): string {
    const stats = this.snapshot?.stats;
    return `${this.countText(stats?.newCustomers || 0, 'new customer joined', 'new customers joined')} in. Excellent taste, frankly.`;
  }

  get customerInterestsSlide(): WrappedSlide | null {
    const interests = this.snapshot?.highlights.topInterests || [];
    if (!interests.length) {
      return null;
    }

    return {
      eyebrow: 'Customer interests',
      title: 'Your crowd had a type',
      metric: interests[0],
      textMetric: true,
      detail: `Their top interests were ${this.formatList(interests)}. Useful intel for the next campaign.`,
    };
  }

  get busiestDayDetail(): string {
    const metrics = this.snapshot?.highlights.busiestDayMetrics;
    if (!metrics) {
      return 'This was the day loyalty activity decided to put on a show.';
    }

    const parts: string[] = [];

    if (metrics.customers) {
      parts.push(
        this.countText(
          metrics.customers,
          'active customer',
          'active customers',
        ),
      );
    }

    if (this.hasStampCards && metrics.stamps) {
      parts.push(this.countText(metrics.stamps, 'stamp', 'stamps'));
    }

    if (this.hasPointCards && metrics.points) {
      parts.push(this.countText(metrics.points, 'point', 'points'));
    }

    if (metrics.rewards) {
      parts.push(
        this.countText(metrics.rewards, 'reward issued', 'rewards issued'),
      );
    }

    if (metrics.redemptions) {
      parts.push(
        this.countText(metrics.redemptions, 'redemption', 'redemptions'),
      );
    }

    if (!parts.length) {
      return 'This was the day loyalty activity decided to put on a show.';
    }

    return `${parts.join(', ')} made this the day the graph quietly showed off.`;
  }

  get voucherOrGrowthSlide(): WrappedSlide {
    const vouchers = this.snapshot?.highlights.giftVouchers;

    if (vouchers?.count) {
      return {
        eyebrow: 'Gift vouchers',
        title: 'People literally gifted your business',
        metric: vouchers.count.toLocaleString(),
        detail: `${this.formatCurrency(vouchers.totalSold)} in ${this.wordFor(vouchers.count, 'voucher sale', 'voucher sales')} came through. Future customers, already queued up.`,
      };
    }

    return {
      eyebrow: 'New faces',
      title: 'The regulars recruited backup',
      metric: (this.snapshot?.stats.newCustomers || 0).toLocaleString(),
      detail: `${this.wordFor(this.snapshot?.stats.newCustomers || 0, 'new customer joined', 'new customers joined')} your loyalty community. The plot thickens.`,
    };
  }

  get giftedRewardSlide(): WrappedSlide | null {
    const giftedRewards = this.snapshot?.highlights.giftedRewards;

    if (!giftedRewards?.count) {
      return null;
    }

    const topReward = giftedRewards.topReward || 'a reward';
    const recipientText =
      giftedRewards.recipients === 1
        ? 'one lucky recipient'
        : `${giftedRewards.recipients.toLocaleString()} lucky recipients`;
    const gifterText = this.countText(
      giftedRewards.gifters,
      'customer gifted rewards',
      'customers gifted rewards',
    );

    return {
      eyebrow: 'Gifted rewards',
      title: 'Customers passed the good stuff on',
      metric: giftedRewards.count.toLocaleString(),
      detail: `${gifterText} to ${recipientText}. ${topReward} did the most travelling.`,
    };
  }

  get promotionSlide(): WrappedSlide | null {
    const promotions = this.snapshot?.highlights.promotions;

    if (promotions?.count) {
      return {
        eyebrow: 'Promotions',
        title: 'You deployed the deals',
        metric: promotions.count.toLocaleString(),
        detail: `${this.wordFor(promotions.count, 'promotion was', 'promotions were')} live. Customers do enjoy a well-timed nudge.`,
      };
    }

    return null;
  }

  get birthdayOrAutomationSlide(): WrappedSlide {
    const birthdayClub = this.snapshot?.highlights.birthdayClub;
    const automations = this.snapshot?.highlights.automations;
    const birthdayMessages = birthdayClub?.messages ?? 0;
    const birthdayRewards = birthdayClub?.rewards ?? 0;
    const birthdayCount = birthdayMessages || birthdayRewards;

    if (birthdayClub?.enabled || birthdayCount) {
      return {
        eyebrow: 'Birthday Club',
        title: 'You remembered the birthdays',
        metric: birthdayCount.toLocaleString(),
        detail: birthdayMessages
          ? `${this.wordFor(birthdayMessages, 'birthday message was', 'birthday messages were')} sent. Cake not included, but strongly implied.`
          : birthdayRewards
            ? `${this.wordFor(birthdayRewards, 'birthday reward was', 'birthday rewards were')} created. Cake not included, but strongly implied.`
            : 'Birthday Club was switched on and ready to make customers feel seen.',
      };
    }

    if (automations?.count) {
      return {
        eyebrow: 'Campaigns',
        title: 'Your automations did the quiet work',
        metric: automations.count.toLocaleString(),
        detail: `${this.countText(automations.recipients, 'customer was', 'customers were')} reached while everyone else got on with their day.`,
      };
    }

    return {
      eyebrow: 'Recognition',
      title: 'Rewards kept things personal',
      metric: (this.snapshot?.stats.redemptions || 0).toLocaleString(),
      detail:
        'customers came back to claim what they earned. A very reasonable life choice.',
    };
  }

  get campaignSlide(): WrappedSlide | null {
    const campaigns = this.snapshot?.highlights.campaignMessages;

    if (campaigns?.count) {
      return {
        eyebrow: 'Campaign messages',
        title: 'You sent the bat signal',
        metric: campaigns.count.toLocaleString(),
        detail: `${this.countText(campaigns.recipients, 'recipient was', 'recipients were')} included across your sends. Subtle? No. Useful? Yes.`,
      };
    }

    return null;
  }

  get finalSlide(): WrappedSlide {
    const unredeemedRewards = this.snapshot?.stats.unredeemedRewards || 0;
    const promotionPrompt = this.nextMonthPromotionPrompt;

    if (unredeemedRewards > 0) {
      return {
        eyebrow: 'Until next year',
        title: "Here's to another year with Remy",
        metric: unredeemedRewards.toLocaleString(),
        detail: `${unredeemedRewards === 1 ? 'reward is' : 'rewards are'} still waiting to be redeemed. A timely push notification could bring those customers back, and ${promotionPrompt}.`,
      };
    }

    return {
      eyebrow: 'Until next year',
      title: "Here's to another year with Remy",
      metric: '',
      detail: `${this.snapshot?.merchantName || 'Your business'} gave customers plenty to come back for. Share the moment, then ${promotionPrompt}.`,
    };
  }

  get nextMonthPromotionPrompt(): string {
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    const monthName = nextMonth.toLocaleString('en-GB', { month: 'long' });
    const themes = [
      'a New Year reset',
      'a February treat',
      'the start of spring',
      'the Easter and spring mood',
      'a May bank holiday moment',
      'the start of summer',
      'school-holiday season',
      'the August bank holiday',
      'back-to-routine season',
      'autumn or Halloween',
      'Black Friday or a winter warm-up',
      'the festive season',
    ];

    return `run a ${monthName} promotion around ${themes[nextMonth.getMonth()]}`;
  }

  trackBySlideIndex(index: number): number {
    return index;
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(value || 0);
  }

  formatList(items: string[]): string {
    if (items.length <= 1) {
      return items[0] || '';
    }

    if (items.length === 2) {
      return `${items[0]} and ${items[1]}`;
    }

    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  }

  get hasStampCards(): boolean {
    return (
      this.snapshot?.capabilities?.stamps ??
      (this.snapshot?.stats.stamps || 0) > 0
    );
  }

  get hasPointCards(): boolean {
    return (
      this.snapshot?.capabilities?.points ??
      (this.snapshot?.stats.points || 0) > 0
    );
  }

  get loyaltySlide(): { title: string; metric: string; detail: string } {
    const stats = this.snapshot?.stats;

    if (!stats) {
      return {
        title: 'Customers kept showing up',
        metric: '0',
        detail: 'loyalty moments recorded. Quiet, but still a moment.',
      };
    }

    if (this.hasStampCards && this.hasPointCards) {
      return {
        title: 'Customers kept clocking in',
        metric: stats.stamps.toLocaleString(),
        detail: `${this.wordFor(stats.stamps, 'stamp was', 'stamps were')} collected, plus ${this.countText(stats.points, 'point was', 'points were')} earned. Loyalty was doing cardio.`,
      };
    }

    if (this.hasPointCards) {
      return {
        title: 'Customers kept stacking points',
        metric: stats.points.toLocaleString(),
        detail: `${this.wordFor(stats.points, 'point was', 'points were')} earned by your customers. Tiny numbers, big commitment.`,
      };
    }

    if (this.hasStampCards) {
      return {
        title: 'Customers kept filling the card',
        metric: stats.stamps.toLocaleString(),
        detail: `${this.wordFor(stats.stamps, 'stamp was', 'stamps were')} collected by your customers. The stamp pad understood the assignment.`,
      };
    }

    return {
      title: 'Customers kept checking in',
      metric: stats.customers.toLocaleString(),
      detail: `${this.wordFor(stats.customers, 'customer was', 'customers were')} active during this period. Quietly loyal, which counts.`,
    };
  }

  countText(count: number, singular: string, plural: string): string {
    return `${count.toLocaleString()} ${this.wordFor(count, singular, plural)}`;
  }

  wordFor(count: number, singular: string, plural: string): string {
    return count === 1 ? singular : plural;
  }
}
